import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../config';
import PublicSponsorCard, { PublicSponsorPreview } from './PublicSponsorCard';
import '../css/components/PublicSponsorPreviewGrid.css';

interface PublicSponsorPreviewGridProps {
    // Omit for the unfiltered "10 most recent across the whole database"
    // view; pass a niche tag for the "5 most recent in that niche" view.
    niche?: string;
}

// Fetches GET /sponsors/preview (optionally scoped to a niche) and renders
// the results as real-looking PublicSponsorCard grid — the shared data
// layer behind both the homepage "Try it live" widget and every niche
// landing page, so they never drift apart and always show real, current
// data instead of a simplified mock.
const PublicSponsorPreviewGrid: React.FC<PublicSponsorPreviewGridProps> = ({ niche }) => {
    const [items, setItems] = useState<PublicSponsorPreview[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        const url = niche
            ? `${config.backendUrl}sponsors/preview?niche=${encodeURIComponent(niche)}`
            : `${config.backendUrl}sponsors/preview`;
        axios.get(url).then((res) => {
            if (!mounted) return;
            setItems(res.data?.preview || []);
        }).catch(() => {
            if (mounted) setItems([]);
        }).finally(() => {
            if (mounted) setLoading(false);
        });
        return () => { mounted = false; };
    }, [niche]);

    if (loading) {
        return (
            <div className="niche-preview-list niche-preview-list--loading">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading {niche ? `${niche} sponsors` : 'recent sponsors'}…
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="niche-preview-list niche-preview-list--empty">
                No {niche ? `${niche} ` : ''}sponsors yet — check back soon, the database grows daily.
            </div>
        );
    }

    return (
        <div className="placements-grid placements-grid--desktop">
            {items.map((item, idx) => (
                <PublicSponsorCard key={`${item.sponsorName}-${idx}`} item={item} />
            ))}
        </div>
    );
};

export default PublicSponsorPreviewGrid;
